import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import {
  Landmark, Loader2, RefreshCw, ChevronDown, ChevronUp, AlertTriangle,
  TrendingUp, TrendingDown, MinusCircle, Layers, Droplets, Box, Gauge,
  Waves, Coffee, Ruler, BarChart3
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ProPaywall from '../components/ProPaywall';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => {
  if (n === null || n === undefined) return '—';
  if (Math.abs(n) >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (Math.abs(n) >= 1) return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return n.toLocaleString('en-US', { maximumFractionDigits: 4 });
};

// ---- Candlestick chart with analysis overlays (SVG string to avoid JSX instrumentation inside <svg>) ----
const AnalysisChart = ({ candles, modules }) => {
  if (!candles || candles.length === 0) return null;
  const W = 900, H = 380, padL = 8, padR = 74, padT = 12, padB = 20;
  const data = candles.slice(-120);
  let lo = Math.min(...data.map(c => c.l));
  let hi = Math.max(...data.map(c => c.h));
  const pad = (hi - lo) * 0.05;
  lo -= pad; hi += pad;
  const x = (i) => padL + (i / (data.length - 1)) * (W - padL - padR);
  const y = (p) => padT + (1 - (p - lo) / (hi - lo)) * (H - padT - padB);
  const cw = Math.max(2, (W - padL - padR) / data.length * 0.6);
  const plotW = W - padL - padR;

  const obs = (modules?.order_blocks?.blocks || []).filter(b => b.low < hi && b.high > lo);
  const fvgs = (modules?.fair_value_gaps?.gaps || []).filter(g => g.low < hi && g.high > lo);
  const liq = (modules?.liquidity_pools?.zones || []).filter(z => z.status === 'resting' && z.price > lo && z.price < hi);
  const fibs = (modules?.fib_confluence?.zones || []).filter(z => z.low < hi && z.high > lo);
  const vp = modules?.volume_profile || {};
  const wy = modules?.wyckoff || {};

  let s = '';
  fibs.forEach(z => {
    s += `<rect x="${padL}" y="${y(z.high)}" width="${plotW}" height="${Math.max(2, y(z.low) - y(z.high))}" fill="#C5A059" opacity="${0.08 + 0.03 * z.overlap_count}"/>`;
  });
  obs.forEach(b => {
    const c = b.type === 'bullish' ? '#10B981' : '#EF4444';
    s += `<rect x="${padL}" y="${y(b.high)}" width="${plotW}" height="${Math.max(2, y(b.low) - y(b.high))}" fill="${c}" opacity="0.12" stroke="${c}" stroke-opacity="0.3" stroke-dasharray="4 4"/>`;
  });
  fvgs.forEach(g => {
    s += `<rect x="${padL}" y="${y(g.high)}" width="${plotW}" height="${Math.max(2, y(g.low) - y(g.high))}" fill="#3B82F6" opacity="0.12"/>`;
  });
  liq.forEach(z => {
    s += `<line x1="${padL}" x2="${W - padR}" y1="${y(z.price)}" y2="${y(z.price)}" stroke="#F59E0B" stroke-width="1" stroke-dasharray="6 4" opacity="0.7"/>`;
    s += `<text x="${W - padR + 4}" y="${y(z.price) + 3}" fill="#F59E0B" font-size="9">${z.type === 'buy-side' ? 'BSL' : 'SSL'} ${fmt(z.price)}</text>`;
  });
  ['poc', 'vah', 'val'].forEach(k => {
    if (vp[k] && vp[k] > lo && vp[k] < hi) {
      s += `<line x1="${padL}" x2="${W - padR}" y1="${y(vp[k])}" y2="${y(vp[k])}" stroke="#A78BFA" stroke-width="${k === 'poc' ? 1.5 : 1}" ${k === 'poc' ? '' : 'stroke-dasharray="2 3"'} opacity="0.6"/>`;
      s += `<text x="${W - padR + 4}" y="${y(vp[k]) + 3}" fill="#A78BFA" font-size="9">${k.toUpperCase()} ${fmt(vp[k])}</text>`;
    }
  });
  if (wy.range_high && wy.range_high < hi) {
    s += `<line x1="${padL}" x2="${W - padR}" y1="${y(wy.range_high)}" y2="${y(wy.range_high)}" stroke="#C5A059" stroke-width="1.5" opacity="0.8"/>`;
  }
  if (wy.range_low && wy.range_low > lo) {
    s += `<line x1="${padL}" x2="${W - padR}" y1="${y(wy.range_low)}" y2="${y(wy.range_low)}" stroke="#C5A059" stroke-width="1.5" opacity="0.8"/>`;
  }
  data.forEach((c, i) => {
    const up = c.c >= c.o;
    const color = up ? '#10B981' : '#EF4444';
    const tip = `O ${fmt(c.o)} H ${fmt(c.h)} L ${fmt(c.l)} C ${fmt(c.c)}`;
    s += `<g><title>${tip}</title>`;
    s += `<line x1="${x(i)}" x2="${x(i)}" y1="${y(c.h)}" y2="${y(c.l)}" stroke="${color}" stroke-width="1"/>`;
    s += `<rect x="${x(i) - cw / 2}" y="${y(Math.max(c.o, c.c))}" width="${cw}" height="${Math.max(1, Math.abs(y(c.o) - y(c.c)))}" fill="${color}"/>`;
    s += `</g>`;
  });

  const svgMarkup = `<svg viewBox="0 0 ${W} ${H}" style="width:100%;min-width:600px" xmlns="http://www.w3.org/2000/svg">${s}</svg>`;

  return (
    <div className="relative w-full overflow-x-auto" data-testid="analysis-chart">
      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(svgMarkup, { USE_PROFILES: { svg: true, svgFilters: true } }) }} />
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[10px] text-gray-500">
        <span><span className="inline-block w-3 h-2 bg-emerald-500/30 border border-emerald-500/50 mr-1"></span>Bullish OB</span>
        <span><span className="inline-block w-3 h-2 bg-red-500/30 border border-red-500/50 mr-1"></span>Bearish OB</span>
        <span><span className="inline-block w-3 h-2 bg-blue-500/30 mr-1"></span>FVG</span>
        <span><span className="inline-block w-3 h-2 bg-[#C5A059]/30 mr-1"></span>Fib confluence</span>
        <span className="text-amber-500">- - Liquidity</span>
        <span className="text-violet-400">— POC/VA</span>
        <span className="text-[#C5A059]">— Wyckoff range</span>
      </div>
    </div>
  );
};

const ConfidenceBar = ({ value }) => (
  <div className="flex items-center gap-2">
    <div className="w-24 h-1.5 bg-white/10 rounded-full overflow-hidden">
      <div className="h-full rounded-full" style={{
        width: `${value}%`,
        background: value >= 60 ? '#10B981' : value >= 40 ? '#C5A059' : '#6B7280'
      }} />
    </div>
    <span className="text-xs text-gray-400 w-9">{value}%</span>
  </div>
);

const ModuleCard = ({ icon: Icon, title, confidence, children, defaultOpen = false, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-[#111111] border border-white/10 rounded-xl overflow-hidden" data-testid={testId}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.02] transition-colors">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#C5A059]/10 flex items-center justify-center">
            <Icon size={16} className="text-[#C5A059]" />
          </div>
          <span className="font-semibold text-white text-sm">{title}</span>
        </div>
        <div className="flex items-center gap-3">
          <ConfidenceBar value={confidence} />
          {open ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
        </div>
      </button>
      {open && <div className="px-5 pb-5 pt-1 text-sm text-gray-300 space-y-2">{children}</div>}
    </div>
  );
};

const Pill = ({ children, color = 'gold' }) => {
  const colors = {
    gold: 'bg-[#C5A059]/10 text-[#C5A059] border-[#C5A059]/30',
    green: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    red: 'bg-red-500/10 text-red-400 border-red-500/30',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    gray: 'bg-white/5 text-gray-400 border-white/10',
  };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] border ${colors[color]}`}>{children}</span>;
};

export default function InstitutionalAnalysis() {
  const { isPro, getToken } = useAuth();
  const [assets, setAssets] = useState([]);
  const [symbol, setSymbol] = useState('BTC');
  const [timeframe, setTimeframe] = useState('1D');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/analysis/assets`).then(r => setAssets(r.data.assets)).catch(() => {});
  }, []);

  const fetchAnalysis = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/analysis/${symbol}?timeframe=${timeframe}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Analysis failed. Please try again.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, timeframe]);

  useEffect(() => {
    if (isPro) fetchAnalysis();
  }, [isPro, fetchAnalysis]);

  if (!isPro) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="pt-32">
          <ProPaywall feature="Institutional Analysis" />
        </div>
        <Footer />
      </div>
    );
  }

  const m = data?.modules;
  const f = data?.forecast;

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="institutional-analysis-page">
      <Navbar />
      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#C5A059] to-[#D4AF37] flex items-center justify-center">
              <Landmark size={20} className="text-black" />
            </div>
            <h1 className="font-manrope font-bold text-3xl sm:text-4xl text-white">
              Institutional <span className="text-[#C5A059]">Analysis</span>
            </h1>
          </div>
          <p className="text-gray-400 text-sm max-w-2xl">
            Hedge-fund-style technical analysis: Wyckoff, smart money concepts, volume profile, Elliott Wave
            and Fibonacci confluence — combined into a probability-weighted 6–12 month outlook.
          </p>
        </div>

        {/* Disclaimer banner */}
        <div className="flex items-start gap-3 bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3 mb-6" data-testid="analysis-disclaimer">
          <AlertTriangle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-200/70">
            Educational probability model, not financial advice. Past patterns do not guarantee future outcomes.
            When the model has no edge, it will say so plainly.
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex flex-wrap gap-2" data-testid="symbol-selector">
            {assets.map(a => (
              <button key={a.symbol} onClick={() => setSymbol(a.symbol)}
                data-testid={`symbol-btn-${a.symbol}`}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                  symbol === a.symbol
                    ? 'bg-[#C5A059] text-black border-[#C5A059]'
                    : 'bg-[#111111] text-gray-300 border-white/10 hover:border-[#C5A059]/50'
                }`}>
                {a.icon} {a.symbol}
              </button>
            ))}
          </div>
          <div className="flex gap-1 bg-[#111111] border border-white/10 rounded-lg p-1 ml-auto" data-testid="timeframe-selector">
            {['4H', '1D', '1W'].map(tf => (
              <button key={tf} onClick={() => setTimeframe(tf)}
                data-testid={`timeframe-btn-${tf}`}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                  timeframe === tf ? 'bg-[#C5A059] text-black' : 'text-gray-400 hover:text-white'
                }`}>
                {tf}
              </button>
            ))}
          </div>
          <button onClick={fetchAnalysis} disabled={loading}
            data-testid="refresh-analysis-btn"
            className="p-2 rounded-lg bg-[#111111] border border-white/10 text-gray-400 hover:text-[#C5A059] transition-colors">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {loading && !data && (
          <div className="flex items-center justify-center py-24" data-testid="analysis-loading">
            <Loader2 size={32} className="animate-spin text-[#C5A059]" />
            <span className="ml-3 text-gray-400">Running institutional analysis...</span>
          </div>
        )}

        {error && (
          <div className="bg-red-500/5 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm mb-6" data-testid="analysis-error">
            {error}
          </div>
        )}

        {data && m && f && (
          <div className="space-y-6">
            {/* Chart */}
            <div className="bg-[#111111] border border-white/10 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-white font-semibold text-lg">{data.name} · {timeframe}</h2>
                  <p className="text-gray-500 text-xs">
                    Last price <span className="text-[#C5A059] font-semibold">${fmt(data.last_price)}</span>
                    {' · '}{data.candle_count} candles analyzed · cached {new Date(data.generated_at).toLocaleTimeString()}
                  </p>
                </div>
              </div>
              <AnalysisChart candles={data.candles} modules={m} />
            </div>

            {/* Composite forecast */}
            <div className="bg-gradient-to-b from-[#151515] to-[#0D0D0D] border border-[#C5A059]/30 rounded-xl p-6" data-testid="composite-forecast">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 className="text-white font-bold text-lg font-manrope">6–12 Month Probability Forecast</h2>
                <Pill color={f.lean.includes('Bullish') ? 'green' : f.lean.includes('Bearish') ? 'red' : 'gray'}>
                  Model lean: {f.lean}
                </Pill>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                {f.scenarios.map((s) => {
                  const color = s.name === 'Bullish' ? '#10B981' : s.name === 'Bearish' ? '#EF4444' : '#C5A059';
                  const Icon = s.name === 'Bullish' ? TrendingUp : s.name === 'Bearish' ? TrendingDown : MinusCircle;
                  return (
                    <div key={s.name} className="bg-black/40 border border-white/10 rounded-xl p-4" data-testid={`scenario-${s.name.toLowerCase()}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Icon size={16} style={{ color }} />
                          <span className="text-sm font-semibold text-white">{s.name}</span>
                        </div>
                        <span className="text-xl font-bold" style={{ color }}>{s.probability}%</span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-3">
                        <div className="h-full rounded-full" style={{ width: `${s.probability}%`, background: color }} />
                      </div>
                      <p className="text-xs text-gray-500">Target zone</p>
                      <p className="text-sm text-gray-300 font-mono">${fmt(s.target_zone[0])} – ${fmt(s.target_zone[1])}</p>
                      {s.drivers.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {s.drivers.map(d => <Pill key={d} color="gray">{d.replace('_', ' ')}</Pill>)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] text-gray-600">
                Weighted composite of all modules below (signal score {f.signal_score}). Probabilities sum to 100%.
              </p>
            </div>

            {/* Module cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ModuleCard icon={Layers} title="Wyckoff Phase" confidence={m.wyckoff.confidence} defaultOpen testId="module-wyckoff">
                <div className="flex flex-wrap gap-2">
                  <Pill color={m.wyckoff.schematic === 'Accumulation' ? 'green' : 'red'}>{m.wyckoff.schematic}</Pill>
                  <Pill>{m.wyckoff.phase}</Pill>
                </div>
                <p className="text-xs text-gray-500">
                  Range: ${fmt(m.wyckoff.range_low)} – ${fmt(m.wyckoff.range_high)} · price at {Math.round((m.wyckoff.price_position_in_range || 0) * 100)}% of range
                </p>
                {m.wyckoff.events?.map((e, i) => (
                  <p key={i} className="text-xs text-gray-400">
                    ⚡ {e.event} @ ${fmt(e.price)} ({new Date(e.t * 1000).toLocaleDateString()}, {e.volume_ratio}× avg vol)
                  </p>
                ))}
              </ModuleCard>

              <ModuleCard icon={Gauge} title="Volume Profile" confidence={m.volume_profile.confidence} defaultOpen testId="module-volume-profile">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-black/30 rounded-lg p-2"><p className="text-[10px] text-gray-500">POC</p><p className="text-violet-400 font-mono text-sm">${fmt(m.volume_profile.poc)}</p></div>
                  <div className="bg-black/30 rounded-lg p-2"><p className="text-[10px] text-gray-500">VAH</p><p className="text-violet-400 font-mono text-sm">${fmt(m.volume_profile.vah)}</p></div>
                  <div className="bg-black/30 rounded-lg p-2"><p className="text-[10px] text-gray-500">VAL</p><p className="text-violet-400 font-mono text-sm">${fmt(m.volume_profile.val)}</p></div>
                </div>
                <p className="text-xs text-gray-400">Price is currently <span className="text-white">{m.volume_profile.price_position}</span> ({m.volume_profile.lookback_bars}-bar lookback)</p>
              </ModuleCard>

              <ModuleCard icon={Box} title="Order Blocks" confidence={m.order_blocks.confidence} testId="module-order-blocks">
                {m.order_blocks.blocks.length === 0 && <p className="text-xs text-gray-500">No unmitigated order blocks detected.</p>}
                {m.order_blocks.blocks.map((b, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-black/30 rounded-lg px-3 py-2">
                    <Pill color={b.type === 'bullish' ? 'green' : 'red'}>{b.type} OB</Pill>
                    <span className="font-mono text-gray-300">${fmt(b.low)} – ${fmt(b.high)}</span>
                    <span className="text-gray-500">{b.impulse_strength}× ATR impulse</span>
                  </div>
                ))}
              </ModuleCard>

              <ModuleCard icon={BarChart3} title="Fair Value Gaps" confidence={m.fair_value_gaps.confidence} testId="module-fvg">
                {m.fair_value_gaps.gaps.length === 0 && <p className="text-xs text-gray-500">No active (unfilled) FVGs.</p>}
                {m.fair_value_gaps.gaps.map((g, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-black/30 rounded-lg px-3 py-2">
                    <Pill color={g.type === 'bullish' ? 'green' : 'red'}>{g.type} FVG</Pill>
                    <span className="font-mono text-gray-300">${fmt(g.low)} – ${fmt(g.high)}</span>
                    <span className="text-gray-500">{g.status} ({g.fill_pct}%)</span>
                  </div>
                ))}
              </ModuleCard>

              <ModuleCard icon={Droplets} title="Liquidity Pools" confidence={m.liquidity_pools.confidence} testId="module-liquidity">
                <p className="text-xs text-gray-500">
                  {m.liquidity_pools.resting_above} resting pools above price · {m.liquidity_pools.resting_below} below
                </p>
                {m.liquidity_pools.zones.map((z, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-black/30 rounded-lg px-3 py-2">
                    <Pill color={z.type === 'buy-side' ? 'blue' : 'gold'}>{z.label}</Pill>
                    <span className="font-mono text-gray-300">${fmt(z.price)}</span>
                    <span className={z.status === 'resting' ? 'text-amber-400' : 'text-gray-600'}>{z.status} · {z.touches} touches</span>
                  </div>
                ))}
              </ModuleCard>

              <ModuleCard icon={Ruler} title="Fibonacci Confluence" confidence={m.fib_confluence.confidence} testId="module-fib">
                {m.fib_confluence.zones.length === 0 && <p className="text-xs text-gray-500">No multi-swing confluence zones found.</p>}
                {m.fib_confluence.zones.map((z, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-black/30 rounded-lg px-3 py-2">
                    <span className="font-mono text-gray-300">${fmt(z.low)} – ${fmt(z.high)}</span>
                    <Pill>{z.overlap_count} levels overlap</Pill>
                    <span className="text-gray-500">{z.ratios.join(' / ')}</span>
                  </div>
                ))}
              </ModuleCard>

              <ModuleCard icon={Waves} title="Elliott Wave Scenarios" confidence={m.elliott_wave.confidence} testId="module-elliott">
                {m.elliott_wave.scenarios.map((s, i) => (
                  <div key={i} className="bg-black/30 rounded-lg px-3 py-2 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-white font-semibold">{i + 1}. {s.label}</span>
                      <div className="flex items-center gap-2">
                        <Pill color={s.bias === 'bullish' ? 'green' : s.bias === 'bearish' ? 'red' : 'gray'}>{s.bias}</Pill>
                        <span className="text-xs text-[#C5A059] font-semibold">{s.confidence}%</span>
                      </div>
                    </div>
                    <p className="text-[11px] text-gray-500 font-mono">Target: ${fmt(s.target_zone[0])} – ${fmt(s.target_zone[1])}</p>
                    {s.rules_checked?.slice(0, 3).map((r, j) => <p key={j} className="text-[10px] text-gray-600">{r}</p>)}
                  </div>
                ))}
                <p className="text-[10px] text-gray-600 italic">Wave counts are inherently probabilistic — multiple valid interpretations shown.</p>
              </ModuleCard>

              <ModuleCard icon={Coffee} title="Cup & Handle" confidence={m.cup_handle.confidence} testId="module-cup-handle">
                {!m.cup_handle.forming && <Pill color="gray">Not actively forming</Pill>}
                {m.cup_handle.forming && <Pill color="green">Pattern forming</Pill>}
                {m.cup_handle.left_rim && (
                  <p className="text-xs text-gray-500 font-mono">
                    Rims ${fmt(m.cup_handle.left_rim)} / ${fmt(m.cup_handle.right_rim)} · bottom ${fmt(m.cup_handle.bottom)} · target ${fmt(m.cup_handle.projected_target)} · invalidation ${fmt(m.cup_handle.invalidation)}
                  </p>
                )}
                {m.cup_handle.checks?.map((c, i) => (
                  <p key={i} className={`text-[11px] ${c.includes('✓') ? 'text-emerald-400/80' : 'text-gray-500'}`}>{c}</p>
                ))}
              </ModuleCard>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
